import { useEffect, useRef } from "react";
import { ArrowTurnDownLeftIcon } from "@heroicons/react/24/outline";
import { Input, EmptyDropdownItem } from "@components";

const NumberInputWithEnter = ({
  innerRef,
  value,
  onChange,
  onClose,
  showEnterIcon = true,
  filterPrefix,
}) => (
  <div className="relative flex-1">
    <Input
      type="number"
      innerRef={innerRef}
      value={value}
      prefix={filterPrefix || undefined}
      className={showEnterIcon ? "pr-9" : undefined}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onClose?.();
        }
      }}
      onChange={onChange}
    />
    {showEnterIcon && (
      <button
        type="button"
        tabIndex={-1}
        onClick={onClose}
        aria-label="Apply filter"
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
      >
        <ArrowTurnDownLeftIcon className="size-4" />
      </button>
    )}
  </div>
);

const NumberInputByOperator = ({ value, onChange, onClose, filterPrefix }) => {
  const firstInputRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => firstInputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [value?.operator]);

  switch (value?.operator) {
    case "lessThan":
      return (
        <NumberInputWithEnter
          innerRef={firstInputRef}
          value={value?.to || ""}
          onChange={(e) => onChange({ ...value, to: e.target.value, from: "" })}
          onClose={onClose}
          filterPrefix={filterPrefix}
        />
      );
    case "greaterThan":
      return (
        <NumberInputWithEnter
          innerRef={firstInputRef}
          value={value?.from || ""}
          onChange={(e) => onChange({ ...value, from: e.target.value, to: "" })}
          onClose={onClose}
          filterPrefix={filterPrefix}
        />
      );
    case "between":
      return (
        <div className="flex items-center gap-2">
          <NumberInputWithEnter
            innerRef={firstInputRef}
            value={value?.from || ""}
            onChange={(e) => onChange({ ...value, from: e.target.value })}
            onClose={onClose}
            showEnterIcon={false}
            filterPrefix={filterPrefix}
          />
          <span>and</span>
          <NumberInputWithEnter
            value={value?.to || ""}
            onChange={(e) => onChange({ ...value, to: e.target.value })}
            onClose={onClose}
            filterPrefix={filterPrefix}
          />
        </div>
      );
    default:
      return null;
  }
};

const NumberEditor = ({ value, onChange, onClose, filterPrefix }) => {
  const handleOperatorChange = (e) => {
    const newOperator = e.target.value;
    onChange({
      ...value,
      operator: newOperator,
      ...(newOperator === "between"
        ? {}
        : newOperator === "greaterThan"
          ? { to: "" }
          : { from: "" }),
    });
  };

  return (
    <EmptyDropdownItem>
      <div className="p-2">
        <div className="mb-2">
          <select
            className="border rounded px-2 py-1 w-full"
            value={value?.operator || "is between"}
            onChange={handleOperatorChange}
          >
            <option value="greaterThan">is more than</option>
            <option value="lessThan">is less than</option>
            <option value="between">is between</option>
          </select>
        </div>
        <NumberInputByOperator
          value={value}
          onChange={onChange}
          onClose={onClose}
          filterPrefix={filterPrefix}
        />
      </div>
    </EmptyDropdownItem>
  );
};

export default NumberEditor;
